import { useCallback, useEffect, useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { BackLink, ConfirmDialog, useToast } from '@/components/play'
import { SiteFooter } from '@/components/SiteFooter'
import { SiteHeader } from '@/components/SiteHeader'
import { isViewer, withoutOrganization } from '@/components/auth/isViewer'
import { ContentSkeleton } from '@/components/loading/Skeleton'
import { useMyInvitations } from '@/lib/invites/useMyInvitations'
import { useAuth } from '@/state/AuthContext'
import { useOnboardingAnalytics } from '@/components/onboarding/useOnboardingAnalytics'
import '@/components/onboarding/onboarding.css'
import type { EventResource, EventsIndexResponse } from '@/types/events'
import { api } from '@/util/api'
import { CreateEventForm } from '@/pages/dashboard/CreateEventDialog'
import { EventLibrarySection } from './dashboard/EventLibrarySection'
import { PendingInvitations } from './dashboard/PendingInvitations'
import { EVENTS_PER_PAGE, initialPagination } from './dashboard/constants'
import type { EventFilter, EventSort, NewEvent } from './dashboard/types'
import { DEFAULT_INGEST_REGION } from '@/hooks/useIngestRegions'

const RENAME_ERROR_TOAST_ID = 'dashboard-rename-error'
const DELETE_FAILED = "We couldn't delete the event. Try again."
/** Creating an event needs an organization: an account without one names it first, then comes back to create. */
const START_HOSTING_TO_CREATE = `/start-hosting?${new URLSearchParams({ return_to: '/app?create=1' })}`

/**
 * Your events (`/app`): manager invitations waiting for this account, then its events, those it manages for another
 * organization included (docs/features/event-managers.md). Creating events stays with organizations: an account
 * without one goes to start hosting instead. A viewer (no organization, no managed events) is let in only when an
 * invitation waits for them (PrivateRoute's landing check), and sees just the invitations.
 */
export function DashboardPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const track = useOnboardingAnalytics()
  const { toast, dismiss } = useToast()
  const { organization, user } = useAuth()
  const viewer = isViewer(organization, user)
  const organizationless = withoutOrganization(organization, user)

  const [events, setEvents] = useState<EventResource[]>([])
  const [pagination, setPagination] = useState(initialPagination)
  const [eventPage, setEventPage] = useState(1)
  const [eventSort, setEventSort] = useState<EventSort>('newest')
  const [eventFilter, setEventFilter] = useState<EventFilter>('all')
  const [eventSearchInput, setEventSearchInput] = useState('')
  const [eventQuery, setEventQuery] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [showNew, setShowNew] = useState(searchParams.get('create') === '1')
  const [form, setForm] = useState<NewEvent>({ name: '', ingest_region: DEFAULT_INGEST_REGION })
  const [creatingEvent, setCreatingEvent] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)
  const loadRequestRef = useRef(0)

  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingName, setEditingName] = useState<string>('')

  // The target stays set while the confirmation closes, so its title doesn't change mid-animation.
  const [deleteTarget, setDeleteTarget] = useState<EventResource | null>(null)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const load = useCallback(async () => {
    const requestId = loadRequestRef.current + 1
    loadRequestRef.current = requestId
    setLoading(true)
    setError(null)
    try {
      const params: Record<string, string | number> = {
        page: eventPage,
        per_page: EVENTS_PER_PAGE,
        sort: eventSort,
      }

      if (eventFilter !== 'all') params.event_type = eventFilter
      if (eventQuery) params.query = eventQuery

      const res = await api.get<EventsIndexResponse>('/events', params)
      if (loadRequestRef.current !== requestId) return

      setEvents(res.data)
      setPagination(
        res.meta?.pagination ?? {
          current_page: eventPage,
          next_page: null,
          prev_page: eventPage > 1 ? eventPage - 1 : null,
          total_pages: 1,
          total_count: res.data.length,
          per_page: EVENTS_PER_PAGE,
        },
      )
    } catch (err: any) {
      if (loadRequestRef.current !== requestId) return
      setError(err?.response?.data?.error?.message || 'Check your connection and try again.')
    } finally {
      if (loadRequestRef.current === requestId) setLoading(false)
    }
  }, [eventFilter, eventPage, eventQuery, eventSort])

  useEffect(() => {
    // A viewer has no events to list, only invitations.
    if (!viewer) load()
  }, [load, viewer])

  const invitations = useMyInvitations()

  useEffect(() => {
    if (showNew) setCreateError(null)
  }, [showNew])

  useEffect(() => {
    const nextQuery = eventSearchInput.trim()
    const handle = window.setTimeout(() => {
      setEventPage(1)
      setEventQuery(nextQuery)
    }, 250)

    return () => window.clearTimeout(handle)
  }, [eventSearchInput])

  useEffect(() => {
    if (
      !loading &&
      pagination.total_pages > 0 &&
      eventPage > pagination.total_pages
    ) {
      setEventPage(pagination.total_pages)
    }
  }, [eventPage, loading, pagination.total_pages])

  async function createEvent(e: FormEvent) {
    e.preventDefault()
    if (!form.name.trim() || creatingEvent) return

    setCreatingEvent(true)
    setCreateError(null)
    try {
      const res = await api.post<{ data: EventResource }>('/events', {
        event: { name: form.name.trim(), ingest_region: form.ingest_region },
      })
      setShowNew(false)
      setForm({ name: '', ingest_region: DEFAULT_INGEST_REGION })
      if (res.data?.id) {
        track('event_created', { event_id: res.data.id })
        navigate(`/events/${res.data.id}`)
      } else {
        load()
      }
    } catch (err: any) {
      setCreateError(
        err?.response?.data?.error?.errors?.join('\n') ||
          "We couldn't create the event. Try again.",
      )
    } finally {
      setCreatingEvent(false)
    }
  }

  async function startEdit(ev: EventResource) {
    dismiss(RENAME_ERROR_TOAST_ID)
    setEditingId(ev.id)
    setEditingName(ev.attributes.name as string)
  }

  // Leaving a rename also clears its failure, so the error never outlives the draft it describes.
  function setRenameTarget(id: string | null) {
    if (id === null) dismiss(RENAME_ERROR_TOAST_ID)
    setEditingId(id)
  }

  async function saveEdit(ev: EventResource) {
    try {
      await api.patch(`/events/${ev.id}`, { event: { name: editingName } })
      dismiss(RENAME_ERROR_TOAST_ID)
      setEditingId(null)
      setEditingName('')
      load()
    } catch (err: any) {
      // A toast doesn't take focus, so the draft and the caret stay in the rename field for a retry.
      // The shared id replaces an earlier failure instead of stacking another.
      toast({
        id: RENAME_ERROR_TOAST_ID,
        title: 'Event not renamed',
        description:
          err?.response?.data?.error?.errors?.join('\n') ||
          'Check your connection and try again.',
        tone: 'error',
      })
    }
  }

  function requestDelete(ev: EventResource) {
    setDeleteTarget(ev)
    setDeleteError(null)
    setDeleteOpen(true)
  }

  async function deleteEvent(ev: EventResource) {
    try {
      await api.delete(`/events/${ev.id}`)
    } catch (err: any) {
      setDeleteError(err?.response?.data?.error?.message || DELETE_FAILED)
      throw err
    }
    if (events.length === 1 && eventPage > 1) {
      setEventPage((page) => page - 1)
    } else {
      load()
    }
  }

  const handleEventSortChange = (value: string) => {
    setEventSort(value as EventSort)
    setEventPage(1)
  }

  const handleEventFilterChange = (value: string) => {
    setEventFilter(value as EventFilter)
    setEventPage(1)
  }

  const clearEventFilters = () => {
    setEventSearchInput('')
    setEventQuery('')
    setEventFilter('all')
    setEventPage(1)
  }

  const hasActiveEventFilters = eventQuery.length > 0 || eventFilter !== 'all'
  const totalPages = Math.max(pagination.total_pages, 1)
  const canGoPrevious = Boolean(pagination.prev_page)
  const canGoNext = Boolean(pagination.next_page)

  const firstEvent =
    !organizationless && !loading && !error && !hasActiveEventFilters && pagination.total_count === 0
  const creating = !organizationless && (showNew || firstEvent)

  if (organizationless && searchParams.get('create') === '1') return <Navigate to={START_HOSTING_TO_CREATE} replace />
  // Every invitation taken or gone: a viewer has nothing here.
  if (viewer && !invitations.loading && !invitations.invitations.length) return <Navigate to="/" replace />

  const pendingInvitations = (
    <PendingInvitations invitations={invitations.invitations} onGone={invitations.drop} heading={!viewer} />
  )

  return (
    <div className="play-theme play-entry">
      <SiteHeader />
      <main className="play-events-page">
        {creating ? (
          <div className="play-create-event">
            {!firstEvent && (
              // Closes the form and drops ?create=1. Held while the event is created, which would open it anyway.
              <BackLink
                to="/app"
                replace
                className="mb-8"
                disabled={creatingEvent}
                onClick={() => setShowNew(false)}
              >
                Events
              </BackLink>
            )}
            {/* An account's first visit may be for an invitation: it comes before naming an event of its own. */}
            {firstEvent && pendingInvitations}
            <p className="play-eyebrow">Your next great race</p>
            <h1 className="mb-3 text-4xl font-black sm:text-6xl">
              Give it a name.
            </h1>
            <p className="mb-10 text-lg text-neutral-7">
              One event. Every camera. All together.
            </p>
            <CreateEventForm
              form={form}
              setForm={setForm}
              createEvent={createEvent}
              isCreating={creatingEvent}
              error={createError}
            />
          </div>
        ) : viewer ? (
          <>
            <h1 className="mb-10 text-4xl font-black md:text-6xl">
              Your invites.
            </h1>
            {invitations.loading ? <ContentSkeleton label="Loading your invites" variant="list" className="py-8" /> : pendingInvitations}
          </>
        ) : (
          <>
            <h1 className="mb-10 text-4xl font-black md:text-6xl">
              Your events.
            </h1>
            {pendingInvitations}
            <EventLibrarySection
              events={events}
              pagination={pagination}
              loading={loading}
              error={error}
              eventSearchInput={eventSearchInput}
              setEventSearchInput={setEventSearchInput}
              eventFilter={eventFilter}
              eventSort={eventSort}
              handleEventFilterChange={handleEventFilterChange}
              handleEventSortChange={handleEventSortChange}
              clearEventFilters={clearEventFilters}
              hasActiveEventFilters={hasActiveEventFilters}
              totalPages={totalPages}
              canGoPrevious={canGoPrevious}
              canGoNext={canGoNext}
              setShowNew={setShowNew}
              createTo={organizationless ? START_HOSTING_TO_CREATE : undefined}
              editingId={editingId}
              editingName={editingName}
              setEditingName={setEditingName}
              setEditingId={setRenameTarget}
              startEdit={startEdit}
              saveEdit={saveEdit}
              deleteEvent={requestDelete}
              setEventPage={setEventPage}
              onRetry={load}
            />
          </>
        )}
      </main>
      <SiteFooter />
      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={`Delete event "${deleteTarget?.attributes.name ?? ''}"?`}
        confirmLabel="Delete event"
        onConfirm={() => (deleteTarget ? deleteEvent(deleteTarget) : undefined)}
        failureMessage={deleteError ?? DELETE_FAILED}
      />
    </div>
  )
}
